import React from 'react'
import clsx from 'clsx'
import Button from '../../common/form/inputs/Button'
import ProfilePic from '../../common/person/ProfilePic'
import UserLabel from '../../common/person/UserLabel'
import type {
    DirectoryUser,
    InviteRow,
} from '../../common/form/inputs/PeoplePickerInput'
import type { CsPersonSearchResultDto } from '../../../api/person/models/searchCs/SearchPersonsCsDtos'

export type SearchMode = 'full_name' | 'li_url'

export type DecoratedPersonResult = CsPersonSearchResultDto & {
    fullName: string
    isColleague: boolean
}

export type SearchState = {
    query: string
    mode: SearchMode
    results: DecoratedPersonResult[]
    loading: boolean
    error?: string
    hasSearched: boolean
}

export type PendingInviteRow = InviteRow & {
    selectedPerson?: DecoratedPersonResult
}

type InviteReviewRowProps = {
    row: PendingInviteRow
    directory: DirectoryUser[]
    onRemove: (email: string) => void
    searchState: SearchState
    onChangeSearchMode: (email: string, mode: SearchMode) => void
    onChangeQuery: (email: string, value: string) => void
    onSearch: (email: string) => void
    onSelectPerson: (email: string, person: DecoratedPersonResult) => void
    onClearSelection: (email: string) => void
    searchInputRefs: React.MutableRefObject<
        Record<string, HTMLInputElement | null>
    >
    disableInteractions: boolean
}

export default function InviteReviewRow({
    row,
    directory,
    onRemove,
    searchState,
    onChangeSearchMode,
    onChangeQuery,
    onSearch,
    onSelectPerson,
    onClearSelection,
    searchInputRefs,
    disableInteractions,
}: InviteReviewRowProps): React.JSX.Element {
    const directoryMatch = directory.find(
        (u) => u.email.toLowerCase() === row.email.toLowerCase(),
    )
    const baseStatusClasses =
        'bg-emerald-50 text-emerald-700 border border-emerald-200'

    if (row.status === 'existing') {
        return (
            <div className="w-full border rounded-xl p-3">
                <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0 space-y-1">
                        <UserLabel
                            name={row.name}
                            email={row.email}
                            headline={directoryMatch?.headline}
                            avatarUrl={directoryMatch?.avatarUrl}
                            size={36}
                            truncate
                        />
                    </div>
                    <button
                        className="ml-auto text-gray-500 hover:bg-gray-100 p-2 rounded-full w-8 h-8 flex items-center justify-center"
                        onClick={() => onRemove(row.email)}
                        aria-label={`Remove ${row.email}`}
                    >
                        ✕
                    </button>
                </div>
            </div>
        )
    }

    let statusLabel: string | undefined
    let statusColourClasses = baseStatusClasses

    if (!row.selectedPerson) {
        statusLabel = row.status === 'error' ? 'Lookup failed' : 'Needs profile'

        if (statusLabel === 'Needs profile') {
            statusColourClasses =
                'bg-orange-50 text-orange-700 border border-orange-200'
        }
    }

    const showColleagueBadge = Boolean(row.selectedPerson?.isColleague)

    const primaryIdentity = row.selectedPerson ? (
        <div className="flex items-center gap-3 min-w-0">
            <ProfilePic
                size={36}
                src={row.selectedPerson.linkedInProfilePicUrl}
                alt={row.selectedPerson.fullName}
            />
            <div className="min-w-0 space-y-0.5">
                <div className="flex items-center gap-2 min-w-0">
                    <div className="text-sm font-semibold text-[#1f2937] truncate">
                        {row.selectedPerson.fullName}
                    </div>
                    {showColleagueBadge && (
                        <span className="text-[10px] uppercase font-semibold text-sky-700 bg-sky-100 rounded-full px-2 py-0.5 whitespace-nowrap">
                            Colleague
                        </span>
                    )}
                </div>
                <div className="text-xs text-gray-500 truncate">
                    {row.email}
                </div>
                {row.selectedPerson.headline && (
                    <div className="text-xs text-gray-600 truncate">
                        {row.selectedPerson.headline}
                    </div>
                )}
            </div>
        </div>
    ) : (
        <UserLabel
            name={row.name}
            email={row.email}
            avatarUrl={directoryMatch?.avatarUrl}
            size={36}
            truncate
        />
    )

    const searchPlaceholder =
        searchState.mode === 'li_url'
            ? 'Paste LinkedIn profile URL'
            : 'Search by full name'

    const showResults = searchState.results.length > 0 && !row.selectedPerson
    const trimmedQuery = searchState.query.trim()
    const canTriggerSearch =
        !disableInteractions && !searchState.loading && Boolean(trimmedQuery)

    return (
        <div className="w-full border rounded-xl p-3">
            <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">{primaryIdentity}</div>
                {(statusLabel || row.selectedPerson) && (
                    <div className="ml-auto flex items-center gap-2">
                        {row.selectedPerson && (
                            <button
                                type="button"
                                className="text-xs text-sky-700 hover:text-sky-900 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                onClick={() => onClearSelection(row.email)}
                                disabled={disableInteractions}
                            >
                                Change profile
                            </button>
                        )}
                        {statusLabel && (
                            <span
                                className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${statusColourClasses}`}
                            >
                                {statusLabel}
                            </span>
                        )}
                    </div>
                )}
                <button
                    className="text-gray-500 hover:bg-gray-100 p-2 rounded-full w-8 h-8 flex items-center justify-center"
                    onClick={() => onRemove(row.email)}
                    aria-label={`Remove ${row.email}`}
                >
                    ✕
                </button>
            </div>

            {!row.selectedPerson && (
                <div className="mt-3 space-y-3">
                    <div className="space-y-2">
                        <div className="flex flex-wrap gap-2">
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    className={clsx(
                                        'px-2 py-1 text-xs font-semibold rounded-full border transition-colors',
                                        searchState.mode === 'full_name'
                                            ? 'bg-sky-100 border-sky-300 text-sky-700'
                                            : 'border-gray-300 text-gray-600 hover:border-gray-400',
                                    )}
                                    onClick={() =>
                                        onChangeSearchMode(
                                            row.email,
                                            'full_name',
                                        )
                                    }
                                    disabled={disableInteractions}
                                >
                                    Full name
                                </button>
                                <button
                                    type="button"
                                    className={clsx(
                                        'px-2 py-1 text-xs font-semibold rounded-full border transition-colors',
                                        searchState.mode === 'li_url'
                                            ? 'bg-sky-100 border-sky-300 text-sky-700'
                                            : 'border-gray-300 text-gray-600 hover:border-gray-400',
                                    )}
                                    onClick={() =>
                                        onChangeSearchMode(row.email, 'li_url')
                                    }
                                    disabled={disableInteractions}
                                >
                                    LinkedIn URL
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                            <input
                                id={`search-${row.email}`}
                                type="text"
                                className="form-input flex-1"
                                placeholder={searchPlaceholder}
                                value={searchState.query}
                                onChange={(e) =>
                                    onChangeQuery(row.email, e.target.value)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault()
                                        if (canTriggerSearch) {
                                            onSearch(row.email)
                                        }
                                    }
                                }}
                                ref={(el) => {
                                    searchInputRefs.current[
                                        row.email.toLowerCase()
                                    ] = el
                                }}
                                disabled={disableInteractions}
                            />
                            <Button
                                type="button"
                                style="secondary"
                                onClick={() => onSearch(row.email)}
                                enabled={canTriggerSearch}
                                classes="sm:w-auto"
                            >
                                {searchState.loading ? 'Searching…' : 'Search'}
                            </Button>
                        </div>

                        {(row.error || searchState.error) && (
                            <div className="text-red-500 text-sm">
                                {row.error || searchState.error}
                            </div>
                        )}

                        {showResults && (
                            <div className="border rounded-lg divide-y max-h-72 overflow-y-auto">
                                {searchState.results.map((person) => (
                                    <button
                                        key={person.id}
                                        type="button"
                                        className="w-full px-3 py-2 flex items-start gap-3 text-left hover:bg-sky-50 focus:bg-sky-50"
                                        onClick={() =>
                                            onSelectPerson(row.email, person)
                                        }
                                        disabled={disableInteractions}
                                    >
                                        <ProfilePic
                                            size={36}
                                            src={person.linkedInProfilePicUrl}
                                            alt={person.fullName}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-sm text-[#1f2937] truncate">
                                                {person.fullName}
                                            </div>
                                            {person.headline && (
                                                <div className="text-xs text-gray-600 truncate">
                                                    {person.headline}
                                                </div>
                                            )}
                                        </div>
                                        {person.isColleague && (
                                            <span className="text-[10px] uppercase font-semibold text-sky-700 bg-sky-100 rounded-full px-2 py-0.5">
                                                Colleague
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
